import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RoleManagementService, Role, RoleListResponse, RoleCreateRequest, RoleUpdateRequest } from '../../services/role-management.service';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-role-management',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './role-management.component.html',
    styleUrls: ['./role-management.component.scss']
})
export class RoleManagementComponent implements OnInit {
    roles: Role[] = [];
    totalRoles = 0;
    currentPage = 1;
    pageSize = 10;
    totalPages = 0;

    // Modal states
    showCreateModal = false;
    showEditModal = false;
    showDeleteModal = false;
    showStatusModal = false;

    // Selected role for operations
    selectedRole: Role | null = null;

    // Form data
    createForm = {
        name: '',
        description: '',
        permissions: {} as { [key: string]: string[] }
    };

    editForm = {
        name: '',
        description: '',
        permissions: {} as { [key: string]: string[] },
        is_active: true
    };

    statusForm = {
        is_active: true
    };

    // Available permissions organized by category
    availablePermissions: { [key: string]: string[] } = {
        'users': ['create', 'read', 'update', 'delete'],
        'roles': ['create', 'read', 'update', 'delete'],
        'projects': ['create', 'read', 'update', 'delete', 'delete_own'],
        'profile': ['read', 'update'],
        'system': ['admin_access']
    };

    // Loading states
    loading = false;
    saving = false;

    // Error handling
    errorMessage = '';
    successMessage = '';

    constructor(
        private roleService: RoleManagementService,
        private authService: AuthService
    ) { }

    ngOnInit(): void {
        this.loadRoles();
    }

    loadRoles(): void {
        this.loading = true;
        const skip = (this.currentPage - 1) * this.pageSize;

        this.roleService.getAllRoles(skip, this.pageSize, false).subscribe({
            next: (response: RoleListResponse) => {
                this.roles = response.roles;
                this.totalRoles = response.total;
                this.totalPages = Math.ceil(this.totalRoles / this.pageSize);
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading roles:', error);
                this.errorMessage = 'Failed to load roles';
                this.loading = false;
            }
        });
    }

    openCreateModal(): void {
        this.createForm = {
            name: '',
            description: '',
            permissions: {}
        };
        this.showCreateModal = true;
        this.clearMessages();
    }

    openEditModal(role: Role): void {
        this.selectedRole = role;
        this.editForm = {
            name: role.name,
            description: role.description,
            permissions: { ...role.permissions }, // Copy the permissions object
            is_active: role.is_active
        };
        this.showEditModal = true;
        this.clearMessages();
    }

    openDeleteModal(role: Role): void {
        this.selectedRole = role;
        this.showDeleteModal = true;
        this.clearMessages();
    }

    openStatusModal(role: Role): void {
        this.selectedRole = role;
        this.statusForm = {
            is_active: role.is_active
        };
        this.showStatusModal = true;
        this.clearMessages();
    }

    closeModals(): void {
        this.showCreateModal = false;
        this.showEditModal = false;
        this.showDeleteModal = false;
        this.showStatusModal = false;
        this.selectedRole = null;
        this.clearMessages();
    }

    createRole(): void {
        this.saving = true;
        const roleRequest: RoleCreateRequest = {
            name: this.createForm.name,
            description: this.createForm.description,
            permissions: this.createForm.permissions
        };

        this.roleService.createRole(roleRequest).subscribe({
            next: (newRole) => {
                this.roles.unshift(newRole);
                this.totalRoles++;
                this.successMessage = 'Role created successfully';
                this.saving = false;
                this.closeModals();
            },
            error: (error) => {
                console.error('Error creating role:', error);
                this.errorMessage = 'Failed to create role';
                this.saving = false;
            }
        });
    }

    updateRole(): void {
        if (!this.selectedRole) return;

        this.saving = true;
        const roleRequest: RoleUpdateRequest = {
            name: this.editForm.name,
            description: this.editForm.description,
            permissions: this.editForm.permissions,
            is_active: this.editForm.is_active
        };

        this.roleService.updateRole(this.selectedRole.id, roleRequest).subscribe({
            next: (updatedRole) => {
                const index = this.roles.findIndex(r => r.id === this.selectedRole!.id);
                if (index !== -1) {
                    this.roles[index] = updatedRole;
                }
                this.successMessage = 'Role updated successfully';
                this.saving = false;
                this.closeModals();
            },
            error: (error) => {
                console.error('Error updating role:', error);
                this.errorMessage = 'Failed to update role';
                this.saving = false;
            }
        });
    }

    deleteRole(): void {
        if (!this.selectedRole) return;

        this.saving = true;
        this.roleService.deleteRole(this.selectedRole.id).subscribe({
            next: () => {
                this.roles = this.roles.filter(r => r.id !== this.selectedRole!.id);
                this.totalRoles--;
                this.successMessage = 'Role deleted successfully';
                this.saving = false;
                this.closeModals();
            },
            error: (error) => {
                console.error('Error deleting role:', error);
                this.errorMessage = 'Failed to delete role';
                this.saving = false;
            }
        });
    }

    toggleRoleStatus(): void {
        if (!this.selectedRole) return;

        this.saving = true;
        this.roleService.toggleRoleStatus(this.selectedRole.id, this.statusForm.is_active).subscribe({
            next: () => {
                const index = this.roles.findIndex(r => r.id === this.selectedRole!.id);
                if (index !== -1) {
                    this.roles[index].is_active = this.statusForm.is_active;
                }
                this.successMessage = `Role ${this.statusForm.is_active ? 'activated' : 'deactivated'} successfully`;
                this.saving = false;
                this.closeModals();
            },
            error: (error) => {
                console.error('Error toggling role status:', error);
                this.errorMessage = 'Failed to update role status';
                this.saving = false;
            }
        });
    }

    togglePermission(category: string, permission: string, formType: 'create' | 'edit'): void {
        const form = formType === 'create' ? this.createForm : this.editForm;

        // Initialize category if it doesn't exist
        if (!form.permissions[category]) {
            form.permissions[category] = [];
        }

        const index = form.permissions[category].indexOf(permission);

        if (index > -1) {
            form.permissions[category].splice(index, 1);
        } else {
            form.permissions[category].push(permission);
        }
    }

    isPermissionSelected(category: string, permission: string, formType: 'create' | 'edit'): boolean {
        const form = formType === 'create' ? this.createForm : this.editForm;
        return form.permissions[category]?.includes(permission) || false;
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.loadRoles();
    }

    formatDate(dateString: string): string {
        return new Date(dateString).toLocaleDateString();
    }

    clearMessages(): void {
        this.errorMessage = '';
        this.successMessage = '';
    }

    // Check if current user can perform admin actions
    canManageRoles(): boolean {
        return this.authService.canAccessRoleManagement();
    }

    // Check if role can be modified
    // System roles can be modified by Admins, but regular users cannot modify system roles
    canModifyRole(role: Role): boolean {
        // If current user is Admin, they can modify any role (including system roles)
        if (this.authService.isAdmin()) {
            return true;
        }
        // Non-admin users cannot modify system roles
        return !role.is_system_role;
    }

    // Helper method to convert permissions object to flat array for display
    getPermissionsArray(permissions: { [key: string]: string[] }): string[] {
        const flatPermissions: string[] = [];
        for (const [category, perms] of Object.entries(permissions)) {
            for (const perm of perms) {
                flatPermissions.push(`${category}:${perm}`);
            }
        }
        return flatPermissions;
    }

    // Helper method to get permission categories for template
    getPermissionCategories(): string[] {
        return Object.keys(this.availablePermissions);
    }
}
